import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Popover, Box, Typography, Grid, useMediaQuery } from '@mui/material';
import { updateActivityDuration, updateReservationType, updateTime } from '../../store/slice/Activity.slice';
import { useTheme } from '@emotion/react';
import CircularProgress from '@mui/material/CircularProgress';
import { UsePopoverPosition } from '../../hooks/UsePopoverPosition';

const ReservationType = ({ Ref, isOpen, handleClose, isLoading }) => {
  const anchorEl = Ref;
  const { css } = useSelector((store) => store?.widgetSettings);
  const restypeCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Reservation type"
  )?.fields;

  const [imgErrors, setImgErrors] = useState({});
  const IMG_URL = process.env.REACT_APP_API_LOCAL_IMG_URL;
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:600px)');

  const { reservType, ActivityData, languageWords } = useSelector(
    (store) => store?.Activity
  );
  const dispatch = useDispatch();

  const resetRelatedFields = () => {
    dispatch(updateTime(''));
    dispatch(updateActivityDuration(''));
  };

  const {
    matchesSm,
    anchorOriginVert,
    transformOriginVert,
    paperSx,
    mobileOverrideProps,
  } = UsePopoverPosition(Ref, isOpen, 400);



  return (
    <Popover
      open={isOpen && Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: anchorOriginVert, horizontal: "center" }}
      transformOrigin={{ vertical: transformOriginVert, horizontal: "center" }}
      {...mobileOverrideProps}        
      PaperProps={{
        sx: {
          ...paperSx,                
          backgroundColor: restypeCss?.ReservationTypesBackgoundColor,
        }
      }}
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
    >
      {isLoading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '220px',
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: restypeCss?.ReservationTypesBackgoundColor,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            // Tight on mobile, comfortable on desktop
            p: { xs: 1.5, sm: 2, md: 3 },
            gap: { xs: '0.75rem', sm: '1rem' },
          }}
        >
          {ActivityData?.map((item, i) => {
            const isActive = reservType?.name === item?.name;
            const hasImage = item?.image && !imgErrors[item.idreservationtype];

            return (
              <Box
                key={item.idreservationtype || i}
                onClick={() => {
                  localStorage.setItem('idreservation', item?.idreservationtype);
                  dispatch(updateReservationType(item));
                  resetRelatedFields();
                  handleClose();
                }}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  minHeight: { xs: 'auto', md: '120px' },
                  flexDirection: { xs: 'row', md: 'row' },
                  gap: { xs: '0.5rem', sm: '0.75rem' },
                  backgroundColor: isActive
                    ? restypeCss?.ReservationTypesItemBackgroundColorActive
                    : restypeCss?.ReservationTypesItemBackgroundColor,
                  border: `1px solid ${restypeCss?.ReservationTypesItemBorderColor}`,
                  color: isActive
                    ? restypeCss?.ReservationTypesItemTextColorActive ?? '#fff'
                    : restypeCss?.ReservationTypesItemTextColor ?? '#000',
                  // Compact padding on mobile
                  p: { xs: 1, sm: 1.5 },
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.015)',
                    boxShadow: '0px 4px 14px rgba(0,0,0,0.15)',
                  },
                }}
              >
                <Grid container spacing={{ xs: 1, sm: 1.5 }} >
                  {/* ── Image ── */}
                  {hasImage && (
                    <Grid item xs={12} sm={12} md={4}>
                      <Box
                        sx={{
                          width: '100%',
                          // Fixed height on mobile so it doesn't go too tall
                       //   height: { xs: '80px', sm: '110px', md: '130px' },
                          borderRadius: { xs: '6px', sm: '8px' },
                          overflow: 'hidden',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }}
                      >
                        <img
                          src={`${IMG_URL}/upload/ReservationType${item?.image.replace(/\\/g, '/')}`}
                          alt="activity-type-img"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            transition: 'transform 0.3s ease',
                          }}
                          onLoad={() =>
                            setImgErrors((prev) => ({
                              ...prev,
                              [item.idreservationtype]: false,
                            }))
                          }
                          onError={() =>
                            setImgErrors((prev) => ({
                              ...prev,
                              [item.idreservationtype]: true,
                            }))
                          }
                        />
                      </Box>
                    </Grid>
                  )}

                  {/* ── Text ── */}
                  <Grid item xs={12} sm={12} md={hasImage ? 8 : 12}>
                    <Box
                      sx={{
                        px: { xs: 0.5, sm: 1 },
                        py: { xs: 0.25, sm: 0.5 },
                      }}
                    >
                      <Typography
                        sx={{
                          color: isActive
                            ? restypeCss?.ReservationTypesItemTextColorActive ?? '#FEF5D8'
                            : restypeCss?.ReservationTypesItemTextColor ?? '#000',
                          // Smaller on mobile, normal on desktop
                          fontSize: { xs: '12px', sm: '13px', md: '14px' },
                          fontWeight: 600,
                          lineHeight: 1.4,
                        }}
                      >
                        {item?.languages?.find((p) => p.fieldname === 'name')?.data ||
                          item?.name}
                      </Typography>

                      {item?.languages?.find((p) => p.fieldname === 'info')?.data && (
                        <Typography
                          sx={{
                            fontSize: { xs: '11px', sm: '12px', md: '13px' },
                            mt: { xs: 0.5, sm: 1 },
                            lineHeight: 1.5,
                            color: isActive
                              ? theme?.resType?.textColorActive
                              : theme?.resType?.itemTextColor,
                          }}
                        >
                          {item.languages.find((p) => p.fieldname === 'info')?.data}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </Box>
      )}
    </Popover>
  );
};

export default ReservationType;
